import type { TFunction } from 'i18next';

import { ApiHttpError } from '@/types';

/**
 * 序號兌換錯誤 → 使用者文案(R277;錯誤碼全表 = BE docs/99 §2.1)
 * -----------------------------------------------------------------------------
 * 判準:**只認 `error.code`,不認裸 status**(R272 紀律)—— 403/404 也可能是 CSRF /
 * WAF / 路由不存在,硬說成「序號無效」會把真問題蓋掉。唯二例外:
 *   - 429 status 兜底(slowapi 的 `error.code` 可能是數字 — R252 教訓);
 *   - `RESOURCE_NOT_FOUND` 是 BE **刻意**的通用碼(防枚舉)→ 文案只說「序號無效」,
 *     不多給細節。
 *
 * `REDEEM_LOCKED`(連續失敗 20 次鎖 1 小時)帶 `error.details.retry_after_seconds`
 * → 換算成分鐘給倒數提示;details 形狀不對就退回不帶時間的版本(防禦式)。
 */
export function mapRedeemErrorMessage(err: unknown, t: TFunction<'redeem'>): string {
  if (!(err instanceof ApiHttpError)) return t('errors.generic');
  const code = err.error?.code ?? '';
  const status = err.status;

  if (code === 'REDEEM_CODE_INVALID_FORMAT') return t('errors.invalidFormat');
  if (code === 'RESOURCE_NOT_FOUND') return t('errors.notFound');
  if (code === 'REDEEM_CODE_ALREADY_USED') return t('errors.alreadyUsed');
  if (code === 'REDEEM_CODE_VOID') return t('errors.voided');
  if (code === 'REDEEM_CODE_EXPIRED') return t('errors.expired');
  if (code === 'CURRENT_GROUP_REQUIRED') return t('errors.groupRequired');
  if (code === 'REDEEM_LOCKED' || status === 429) {
    const retryAfter = retryAfterSeconds(err);
    if (retryAfter !== null) {
      return t('errors.locked', { minutes: Math.max(1, Math.ceil(retryAfter / 60)) });
    }
    return t('errors.rateLimited');
  }
  if (code === 'FEATURE_DISABLED') return t('errors.notSupported');

  return `${t('errors.generic')} (${status}${code ? ` ${code}` : ''})`;
}

/** `error.details.retry_after_seconds`(防禦式讀;形狀不對回 null)。 */
function retryAfterSeconds(err: ApiHttpError): number | null {
  const details = err.error?.details;
  if (details == null || typeof details !== 'object') return null;
  const raw = (details as { retry_after_seconds?: unknown }).retry_after_seconds;
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
}
